import jsPDF from "jspdf";

export const normalizeResumeMode = (value, hasUploadedResume = false) => {
  if (value === "upload") return "upload";
  if (value === "auto") return "auto";
  return hasUploadedResume ? "upload" : "auto";
};

const asArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === undefined || value === null || value === "") return [];
  return [value];
};

const formatListValue = (value) => asArray(value).join(", ");

const getOptionId = (option) => String(option?._id || option?.value || option?.id || "");
const getOptionLabel = (option) => option?.name || option?.label || option?.title || "";

export const resolveOptionLabels = (values, options = []) => {
  const optionMap = new Map(
    asArray(options)
      .filter(Boolean)
      .map((option) => [getOptionId(option), getOptionLabel(option)])
  );

  return asArray(values)
    .map((item) => {
      if (typeof item === "object" && item !== null) {
        return getOptionLabel(item) || getOptionId(item);
      }

      const key = String(item);
      return optionMap.get(key) || key;
    })
    .filter(Boolean);
};

const buildSummary = (profile, specializationLabels) => {
  const explicitSummary =
    profile?.about ||
    profile?.ProfessionalAchievements ||
    profile?.achievements ||
    profile?.professionalSummary;

  if (explicitSummary) {
    return explicitSummary;
  }

  const parts = [
    profile?.highestMedicalDegree || profile?.highestEducation,
    profile?.experience !== undefined &&
    profile?.experience !== null &&
    String(profile.experience) !== ""
      ? `${profile.experience}+ years experience`
      : "",
    profile?.employmentType || profile?.preferredJobType,
    formatListValue(profile?.jobLocation || profile?.preferredLocation),
    specializationLabels.length ? `${specializationLabels.join(", ")} specialist` : "",
  ].filter(Boolean);

  return parts.length
    ? `Experienced job seeker with a background in ${parts.join(" | ")}.`
    : "Motivated job seeker with a complete profile and verified contact details.";
};

export const buildJobSeekerResumeData = (profile = {}, options = {}) => {
  const specializationLabels = resolveOptionLabels(
    profile?.specialization || profile?.functionalAreas,
    options.specializationOptions
  );

  const location = [profile?.city, profile?.statename || profile?.state]
    .filter(Boolean)
    .join(", ");

  return {
    name: profile?.username || profile?.name || "Job Seeker",
    email: profile?.email || "",
    phone: profile?.phone || "",
    address: profile?.address || "",
    location,
    pincode: profile?.pincode || "",
    linkedInProfile: profile?.linkedInProfile || "",
    profileUrl: profile?.profile_url || profile?.profileUrl || "",
    experience:
      profile?.experience !== undefined && profile?.experience !== null
        ? String(profile.experience)
        : "",
    highestMedicalDegree:
      profile?.highestMedicalDegree || profile?.highestEducation || "",
    institutionUniversity: profile?.institutionUniversity || profile?.institute || "",
    yearOfGraduation: profile?.yearOfGraduation || "",
    jobLocation: formatListValue(profile?.jobLocation || profile?.preferredLocation),
    employmentType: profile?.employmentType || profile?.preferredJobType || "",
    salaryExpectation: profile?.salaryExpectation || "",
    MedicalRegistrationNumber: profile?.MedicalRegistrationNumber || "",
    issuingMedicalCouncil: profile?.issuingMedicalCouncil || "",
    achievements:
      profile?.ProfessionalAchievements || profile?.achievements || "",
    workExperience: asArray(profile?.WorkExperience || profile?.workExperience).filter(
      (item) => item && (item.title || item.year || item.description)
    ),
    keyAchievementsList: asArray(
      profile?.KeyAchievements || profile?.keyAchievements
    )
      .map((item) => {
        if (typeof item === "string") return item;
        return item?.title || item?.text || "";
      })
      .filter(Boolean),
    summary: buildSummary(profile, specializationLabels),
    specializationLabels,
  };
};

const drawSection = (doc, title, lines, cursorY) => {
  if (!lines.length) return cursorY;

  doc.setDrawColor(220, 226, 230);
  doc.line(14, cursorY, 196, cursorY);
  cursorY += 6;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(title, 14, cursorY);
  cursorY += 6;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);

  lines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line, 180);
    doc.text(wrapped, 14, cursorY);
    cursorY += wrapped.length * 5 + 1;
  });

  return cursorY + 1;
};

export const downloadGeneratedResumePdf = (profile = {}, options = {}) => {
  const data = buildJobSeekerResumeData(profile, options);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let cursorY = 18;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(data.name, 14, cursorY);
  cursorY += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  const topLine = [
    data.email,
    data.phone,
    data.location,
    data.linkedInProfile,
  ].filter(Boolean);
  if (topLine.length) {
    doc.text(doc.splitTextToSize(topLine.join(" | "), 180), 14, cursorY);
    cursorY += 8;
  }

  cursorY = drawSection(doc, "Professional Summary", [data.summary], cursorY);

  cursorY = drawSection(
    doc,
    "Profile Snapshot",
    [
      [data.highestMedicalDegree, data.institutionUniversity, data.yearOfGraduation]
        .filter(Boolean)
        .join(" | "),
      [
        data.experience ? `Experience: ${data.experience}` : "",
        data.employmentType ? `Preferred Job Type: ${data.employmentType}` : "",
        data.jobLocation ? `Preferred Location: ${data.jobLocation}` : "",
        data.salaryExpectation ? `Salary Expectation: ${data.salaryExpectation}` : "",
      ]
        .filter(Boolean)
        .join(" | "),
      [
        data.MedicalRegistrationNumber
          ? `Registration ID: ${data.MedicalRegistrationNumber}`
          : "",
        data.issuingMedicalCouncil
          ? `Medical Council: ${data.issuingMedicalCouncil}`
          : "",
      ]
        .filter(Boolean)
        .join(" | "),
    ].filter(Boolean),
    cursorY
  );

  cursorY = drawSection(
    doc,
    "Functional Areas",
    data.specializationLabels.length
      ? [data.specializationLabels.join(", ")]
      : ["Functional areas not specified."],
    cursorY
  );

  cursorY = drawSection(
    doc,
    "Achievements",
    data.keyAchievementsList.length
      ? data.keyAchievementsList
      : data.achievements
      ? data.achievements
          .split(/\r?\n+/)
          .map((line) => line.trim())
          .filter(Boolean)
      : ["Achievements or additional summary not provided."],
    cursorY
  );

  cursorY = drawSection(
    doc,
    "Work Experience",
    data.workExperience.length
      ? data.workExperience.map((item) =>
          [item.title, item.year, item.description].filter(Boolean).join(" | ")
        )
      : ["Work experience not added yet."],
    cursorY
  );

  drawSection(
    doc,
    "Address",
    [[data.address, data.pincode].filter(Boolean).join(" | ")].filter(Boolean),
    cursorY
  );

  const safeName = String(data.name || "job-seeker")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  doc.save(`${safeName || "job-seeker"}-resume.pdf`);
};
